import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { formatCurrency, formatDate, getStatusySpacer, DARMOWE_OPCJE, statusSpacerMeta } from '../utils';
import Modal from '../components/Modal';
import { Input, Select, Btn } from '../components/Input';
import {
  IconEdit, IconTrash, IconLink, IconPlus, IconSnowflake,
  IconBuilding, IconMoney, IconPackage, IconSearch,
} from '../components/Icons';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import { SkeletonCard } from '../components/Skeleton';
import HelpHint from '../components/HelpHint';
import EmptyState from '../components/EmptyState';

// ─── Form dodawania/edycji spaceru ───────────────────────────────────────────

export function SpacerForm({ initial, klienci, onSave, onClose, lockKlient = false }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [form, setForm] = useState(initial || {
    klient_id: klienci[0]?.id || '',
    nazwa: '', adres: '', link_matterport: '', miniatura: '',
    data_wykonania: new Date().toISOString().slice(0, 10),
    darmowy_okres: 12,
    stawka_hostingu: 15,
    status: 'aktywny',
    kwota_wykonania: '', nr_faktury: '', przelane_wykonanie: false,
    pakiet_id: initial?.pakiet_id || '',
  });
  const [pakiety, setPakiety] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scraping, setScraping] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Pobierz pakiety klienta gdy zmienia się klient
  useEffect(() => {
    if (!form.klient_id) { setPakiety([]); return; }
    api.getPakiety().then(all => {
      setPakiety(all.filter(p => String(p.klient_id) === String(form.klient_id)));
    }).catch(() => setPakiety([]));
  }, [form.klient_id]);

  // Zmiana pakietu — auto-uzupełnij darmowy okres i stawkę
  const handlePakietChange = (pakietId) => {
    set('pakiet_id', pakietId);
    if (!pakietId) return;
    const p = pakiety.find(p => String(p.id) === String(pakietId));
    if (!p) return;
    if (p.miesiac_hostingu_w_cenie > 0) {
      set('darmowy_okres', p.miesiac_hostingu_w_cenie);
    }
    if (p.stawka_hostingu_pakiet != null) {
      set('stawka_hostingu', p.stawka_hostingu_pakiet);
    }
  };

  const handleScrape = async () => {
    const link = form.link_matterport?.trim();
    if (!link) return;
    setScraping(true);
    try {
      const data = await api.scrapeMatterport(link);
      if (data.title && !form.nazwa) set('nazwa', data.title);
      if (data.image) set('miniatura', data.image);
    } catch (e) { toast.error(t('toast.fetchError', { message: e.message })); }
    finally { setScraping(false); }
  };

  const submit = async e => {
    e.preventDefault();
    // Explicit walidacja zamiast natywnego `required` — natywny popup "Uzupełnij pole"
    // bywa niewidoczny gdy pole jest poza viewportem modala, a komponent DateField
    // (maska dd.mm.rrrr) nie zawsze gra ładnie z natywną walidacją form-a.
    if (!form.klient_id)        { toast.error(t('toast.requiredField', { field: t('spaces.client') }));       return; }
    if (!form.nazwa?.trim())    { toast.error(t('toast.requiredField', { field: t('spaces.nameObject') }));   return; }
    if (!form.data_wykonania || !/^\d{4}-\d{2}-\d{2}$/.test(form.data_wykonania)) {
      toast.error(t('toast.requiredField', { field: t('spaces.executionDate') }));
      return;
    }
    setLoading(true);
    try {
      await onSave({
        ...form,
        klient_id: Number(form.klient_id),
        darmowy_okres: Number(form.darmowy_okres),
        stawka_hostingu: Number(form.stawka_hostingu),
        kwota_wykonania: form.kwota_wykonania !== '' ? Number(form.kwota_wykonania) : null,
        pakiet_id: form.pakiet_id ? Number(form.pakiet_id) : null,
      });
      onClose();
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const lockedKlient = lockKlient
    ? klienci.find(k => String(k.id) === String(form.klient_id))
    : null;

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      {lockedKlient ? (
        <div className="flex flex-col gap-1">
          <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
            {t('spaces.client')}
          </label>
          <div
            style={{
              background: 'var(--bg3)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-btn)',
              padding: '8px 12px',
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--ink)',
            }}>
            {lockedKlient.nazwa}
          </div>
        </div>
      ) : (
        <Select label={t('spaces.client')} value={form.klient_id} onChange={e => { set('klient_id', e.target.value); set('pakiet_id', ''); }} required>
          {klienci.map(k => <option key={k.id} value={k.id}>{k.nazwa}</option>)}
        </Select>
      )}

      {/* Pakiet — pokazuj tylko gdy klient ma pakiety */}
      {pakiety.length > 0 && (
        <div className="flex flex-col gap-1">
          <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{t('spaces.assignPackage')}</label>
          <select
            className="ds-input"
            value={form.pakiet_id || ''}
            onChange={e => handlePakietChange(e.target.value)}>
            <option value="">{t('spaces.noPackage')}</option>
            {pakiety.map(p => {
              const used = p.spacery_uzyte || 0;
              const cap  = p.liczba_spacerow || 0;
              const isCurrentPackage = String(p.id) === String(initial?.pakiet_id);
              const full = cap > 0 && used >= cap && !isCurrentPackage;
              return (
                <option key={p.id} value={p.id} disabled={full}>
                  {p.nazwa} — {used}/{cap || '∞'}{full ? ` (${t('spaces.packageFull')})` : ''}
                  {p.miesiac_hostingu_w_cenie > 0 ? ` · ${p.miesiac_hostingu_w_cenie} ${t('clientDetail.hostingMonths')}` : ''}
                </option>
              );
            })}
          </select>
          {form.pakiet_id && (() => {
            const p = pakiety.find(p => String(p.id) === String(form.pakiet_id));
            if (!p) return null;
            const used = p.spacery_uzyte || 0;
            const cap  = p.liczba_spacerow || 0;
            const isCurrentPackage = String(p.id) === String(initial?.pakiet_id);
            const slotsLeft = cap > 0 ? Math.max(0, cap - used - (isCurrentPackage ? 0 : 1)) : null;
            return (
              <p style={{ background: 'rgba(184,84,40,0.08)', border: '1px solid rgba(184,84,40,0.2)', borderRadius: '8px', padding: '8px 12px', fontSize: '10px', color: 'var(--acc2)' }}>
                {t('spaces.package')}: <span style={{ fontWeight: 600 }}>{p.nazwa}</span>
                {cap > 0 && <> · {used}/{cap} {t('spaces.spacery')}{slotsLeft !== null && !isCurrentPackage && ` (${t('spaces.slotsLeftAfterAdd', { n: slotsLeft })})`}</>}
                {p.miesiac_hostingu_w_cenie > 0 && <> · {p.miesiac_hostingu_w_cenie} {t('clientDetail.hostingMonths')} uzupełnione</>}
              </p>
            );
          })()}
        </div>
      )}

      <div className="flex flex-col gap-1">
        <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{t('spaces.matterportLink')}</label>
        <div className="flex gap-2">
          <input
            className="ds-input flex-1"
            value={form.link_matterport || ''}
            onChange={e => set('link_matterport', e.target.value)}
            onBlur={handleScrape}
            placeholder="https://my.matterport.com/show/?m=..."
          />
          <Btn type="button" variant="ghost" onClick={handleScrape} disabled={scraping}>
            {scraping ? '...' : t('spaces.fetchData')}
          </Btn>
        </div>
      </div>

      {form.miniatura && (
        <div className="flex gap-3 items-center">
          <img src={form.miniatura} alt="" className="w-24 h-16 object-cover rounded-lg flex-shrink-0" style={{ border: '1px solid var(--border)' }} />
          <button type="button" onClick={() => set('miniatura', '')} style={{ fontSize: '10px', color: 'var(--err2)' }}>{t('spaces.removeThumbnail')}</button>
        </div>
      )}

      <Input label={t('spaces.nameObject')} value={form.nazwa} onChange={e => set('nazwa', e.target.value)} required />
      <Input label={t('spaces.address')} value={form.adres || ''} onChange={e => set('adres', e.target.value)} />
      <Input label={t('spaces.executionDate')} type="date" value={form.data_wykonania} onChange={e => set('data_wykonania', e.target.value)} required />

      {/* Płatność za wykonanie — ukryta gdy spacer przypisany do pakietu */}
      {!form.pakiet_id ? (
        <div style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '14px', background: 'var(--bg2)' }} className="flex flex-col gap-3">
          <p className="section-label">{t('spaces.executionPayment')}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input label={t('spaces.executionAmount')} type="number" min="0" step="0.01" placeholder="np. 800"
              value={form.kwota_wykonania ?? ''} onChange={e => set('kwota_wykonania', e.target.value)} />
            <Input label={t('spaces.invoiceNumber')} placeholder="np. FV/2025/001"
              value={form.nr_faktury || ''} onChange={e => set('nr_faktury', e.target.value)} />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!form.przelane_wykonanie}
              onChange={e => set('przelane_wykonanie', e.target.checked)}
              className="w-4 h-4 rounded bg-navy-700 border-navy-600 accent-[#b85428]" />
            <span style={{ fontSize: '11px', color: 'var(--ink)' }}>{t('spaces.transferred')}</span>
          </label>
        </div>
      ) : (
        <div style={{ background: 'rgba(184,84,40,0.08)', border: '1px solid rgba(184,84,40,0.2)', borderRadius: '8px', padding: '8px 12px', fontSize: '10px', color: 'var(--acc2)' }}>
          {t('spaces.packageCoveredPayment')}
        </div>
      )}

      {/* Hosting */}
      <div style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '14px', background: 'var(--bg2)' }} className="flex flex-col gap-3">
        <p className="section-label">{t('spaces.hostingRate')}</p>
        <div className="flex flex-col gap-1">
          <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {t('spaces.freeIncluded')}
            <HelpHint text={t('help.freePeriod')} label={t('spaces.freeIncluded')} />
          </label>
          <div className="flex gap-1 flex-wrap">
            {DARMOWE_OPCJE.map(n => (
              <button key={n} type="button" onClick={() => set('darmowy_okres', n)}
                className="px-3 py-1 rounded-btn transition-colors"
                style={Number(form.darmowy_okres) === n
                  ? { background: 'var(--acc)', color: '#fff', border: '1px solid rgba(184,84,40,0.6)', fontSize: '9px', fontWeight: 700 }
                  : { background: 'var(--bg4)', color: 'var(--ink2)', border: '1px solid var(--border)', fontSize: '9px', fontWeight: 700 }}>
                {n === 0 ? 'brak' : `${n} mies.`}
              </button>
            ))}
          </div>
          <input type="number" min="0"
            className="ds-input mt-1"
            value={form.darmowy_okres} onChange={e => set('darmowy_okres', e.target.value)} />
        </div>
        <Input
          label={<span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {t('spaces.hostingRateFull')}
            <HelpHint text={t('help.hostingRate')} label={t('spaces.hostingRateFull')} />
          </span>}
          type="number" min="0" step="0.01"
          value={form.stawka_hostingu} onChange={e => set('stawka_hostingu', e.target.value)} />
      </div>

      <div className="flex flex-col gap-1">
        <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          {t('spaces.status')}
          <HelpHint text={t('help.spaceStatus')} label={t('spaces.status')} />
        </label>
        <div className="flex gap-1 flex-wrap">
          {getStatusySpacer().map(s => {
            const active = form.status === s.value;
            const colors = {
              aktywny:    { bg: 'rgba(90,158,114,0.15)',  bd: 'rgba(90,158,114,0.5)',  fg: 'var(--ok2)' },
              zamrozony:  { bg: 'rgba(74,158,186,0.15)',  bd: 'rgba(74,158,186,0.5)',  fg: '#4a9eba' },
              przekazany: { bg: 'rgba(155,108,200,0.18)', bd: 'rgba(155,108,200,0.5)', fg: '#b48ad8' },
              usuniety:   { bg: 'rgba(176,88,72,0.18)',   bd: 'rgba(176,88,72,0.5)',   fg: 'var(--err2)' },
            }[s.value] || { bg: 'var(--bg4)', bd: 'var(--border)', fg: 'var(--ink2)' };
            return (
              <button key={s.value} type="button" onClick={() => set('status', s.value)}
                className="px-3 py-2 rounded-btn transition-colors flex-1"
                style={active
                  ? { background: colors.bg, color: colors.fg, border: `1px solid ${colors.bd}`, fontSize: '10px', fontWeight: 700 }
                  : { background: 'var(--bg4)', color: 'var(--ink3)', border: '1px solid var(--border)', fontSize: '10px', fontWeight: 600 }}>
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Btn type="button" variant="ghost" onClick={onClose}>{t('common.cancel')}</Btn>
        <Btn type="submit" disabled={loading}>{loading ? t('common.saving') : t('common.save')}</Btn>
      </div>
    </form>
  );
}

// ─── Karta spaceru (uproszczona) ─────────────────────────────────────────────

function SpacerCard({ spacer: s, onEdit, onDelete, onNavigate, defaultKlientId }) {
  const { t } = useTranslation();
  const meta = statusSpacerMeta(s.status);
  const dzis = new Date().toISOString().slice(0, 10);
  const wDarmowym = dzis <= s.koniec_darmowego;

  const handleCardClick = (e) => {
    // Nie naviguj jeśli kliknięto w przycisk lub link
    if (e.target.closest('button') || e.target.closest('a')) return;
    onNavigate('spacer', s.id);
  };

  const statusPillClass = s.status === 'aktywny' ? 'pill pill-ok'
    : s.status === 'zamrozony' ? 'pill pill-blue'
    : s.status === 'usuniety' ? 'pill pill-err'
    : 'pill pill-gray';

  return (
    <div
      className="ds-card overflow-hidden hover:border-navy-500 transition-colors cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="p-3 sm:p-4 flex gap-3 sm:gap-4">
        {/* Miniatura — kwadracik z lewej (mniejsza na mobile) */}
        <div className="relative flex-shrink-0 w-[72px] h-[48px] sm:w-[96px] sm:h-[64px]">
          {s.miniatura ? (
            <img src={s.miniatura} alt={s.nazwa}
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border)', display: 'block' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', borderRadius: '8px', background: 'var(--bg3)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconBuilding style={{ width: '24px', height: '24px', color: 'var(--ink3)' }} />
            </div>
          )}
          {s.link_matterport && (
            <a href={s.link_matterport} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              title={t('spaces.openSpace')}
              style={{ position: 'absolute', bottom: '4px', right: '4px', width: '20px', height: '20px', borderRadius: '4px', background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconLink style={{ width: '11px', height: '11px', color: '#fff' }} />
            </a>
          )}
        </div>

        {/* Dane */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)' }} className="truncate">{s.nazwa}</h3>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0" style={{ marginTop: '2px' }}>
                <p style={{ fontSize: '10px', color: 'var(--ink3)', margin: 0 }}>{formatDate(s.data_wykonania)}</p>
                {s.adres && <p style={{ fontSize: '10px', color: 'var(--ink2)', margin: 0 }} className="truncate">{s.adres}</p>}
              </div>
              {!defaultKlientId && (
                <button onClick={e => { e.stopPropagation(); onNavigate('klient', s.klient_id); }}
                  style={{ fontSize: '10px', color: 'var(--ink2)', marginTop: '2px' }}>
                  {s.klient_nazwa}
                </button>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <span className={`${statusPillClass} flex items-center gap-1`}>
                {s.status === 'zamrozony' && <IconSnowflake className="w-3 h-3" />}
                {meta.label}
              </span>
              {s.pakiet_nazwa && (
                <span className="pill flex items-center gap-1" style={{ background: 'rgba(184,84,40,0.12)', color: 'var(--acc2)', border: '1px solid rgba(184,84,40,0.25)' }}>
                  <IconPackage className="w-3 h-3" /> {s.pakiet_nazwa}
                </span>
              )}
            </div>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-0.5">
            {wDarmowym && s.status === 'aktywny' && <span className="pill pill-ok">{t('spaces.freeperiod')}</span>}
            {!wDarmowym && s.status === 'aktywny' && (
              s.kwota_narosla > 0
                ? <span style={{ fontWeight: 700, color: 'var(--acc2)', fontFamily: 'monospace', fontSize: '11px' }}>{t('spaces.accrued')}: {formatCurrency(s.kwota_narosla)}</span>
                : <span style={{ color: 'var(--ok2)', fontSize: '11px' }}>{t('spaces.noDebts')}</span>
            )}
          </div>
        </div>

        {/* Akcje */}
        <div className="flex flex-col items-end justify-between gap-2 flex-shrink-0">
          <div className="flex gap-1">
            <button onClick={e => { e.stopPropagation(); onEdit(); }}
              className="w-7 h-7 flex items-center justify-center rounded-btn transition-colors hover:bg-navy-800"
              style={{ color: 'var(--ink3)' }} title={t('spaces.editSettings')}>
              <IconEdit className="w-4 h-4" />
            </button>
            <button onClick={e => { e.stopPropagation(); onDelete(); }}
              className="w-7 h-7 flex items-center justify-center rounded-btn transition-colors hover:bg-navy-800"
              style={{ color: 'var(--err2)' }} title={t('common.delete')}>
              <IconTrash className="w-4 h-4" />
            </button>
          </div>
          {s.status === 'aktywny' && (
            <button onClick={e => { e.stopPropagation(); onNavigate('spacer', s.id); }}
              className="flex items-center gap-1 rounded-btn transition-colors hover:bg-navy-800"
              style={{ fontSize: '9px', fontWeight: 700, color: 'var(--acc2)', border: '1px solid rgba(204,104,64,0.3)', padding: '4px 8px' }}
              title={t('spaces.settlement')}>
              <IconMoney className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{t('spaces.settlement')}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Widok listy spacerów ─────────────────────────────────────────────────────

export default function Spacery({ onNavigate, defaultKlientId, initialAction, onActionConsumed, compact = false }) {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();

  const STATUSY_FILTER = [
    { value: 'aktywny',    label: t('spaces.active_filter') },
    { value: '',           label: t('spaces.all') },
    { value: 'zamrozony',  label: t('spaces.frozen_filter') },
    { value: 'przekazany', label: t('spaces.transferred_filter') },
    { value: 'usuniety',   label: t('spaces.deleted_filter') },
  ];

  const [spacery, setSpacery] = useState([]);
  const [klienci, setKlienci] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('aktywny');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [collapsedPackages, setCollapsedPackages] = useState({}); // pakiet_id -> bool

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (defaultKlientId) params.klient_id = defaultKlientId;
    Promise.all([api.getSpacery(params), api.getKlienci()]).then(([s, k]) => {
      setSpacery(s);
      setKlienci(k);
    }).finally(() => setLoading(false));
  }, [defaultKlientId]);

  useEffect(() => { load(); }, [load]);

  // Konsumuj initialAction (np. z onboardingu Dashboard) — otwórz modal raz.
  useEffect(() => {
    if (initialAction === 'add') {
      setModal('add');
      onActionConsumed?.();
    }
  }, [initialAction, onActionConsumed]);

  const handleAdd  = async form => {
    await api.addSpacer(form);
    load();
    toast.success(t('toast.added'));
  };
  const handleEdit = async form => {
    await api.updateSpacer(modal.edit.id, form);
    load();
    toast.success(t('toast.saved'));
  };
  const handleDelete = async s => {
    const ok = await confirm({
      title: t('confirmDialog.deleteTitle'),
      message: t('spaces.deleteConfirm', { name: s.nazwa }),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteSpacer(s.id);
      toast.success(t('toast.deleted'));
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const counts = spacery.reduce((acc, s) => {
    acc.all = (acc.all || 0) + 1;
    acc[s.status] = (acc[s.status] || 0) + 1;
    return acc;
  }, {});

  const filtered = spacery
    .filter(s => !filterStatus || s.status === filterStatus)
    .filter(s =>
      s.nazwa.toLowerCase().includes(search.toLowerCase()) ||
      (s.adres || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.klient_nazwa || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.pakiet_nazwa || '').toLowerCase().includes(search.toLowerCase())
    );

  // Grupuj po pakiecie — pakiety pierwsze, potem solo
  const grouped = (() => {
    const packages = new Map(); // pakiet_id -> { id, nazwa, capacity, items: [] }
    const solo = [];
    filtered.forEach(s => {
      if (s.pakiet_id) {
        if (!packages.has(s.pakiet_id)) {
          packages.set(s.pakiet_id, {
            id: s.pakiet_id,
            nazwa: s.pakiet_nazwa || `Pakiet #${s.pakiet_id}`,
            capacity: s.pakiet_pojemnosc || 0,
            klient_nazwa: s.klient_nazwa,
            items: [],
          });
        }
        packages.get(s.pakiet_id).items.push(s);
      } else {
        solo.push(s);
      }
    });
    return { packages: Array.from(packages.values()), solo };
  })();

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--ink)' }}>{t('spaces.title')}</h1>
        <Btn onClick={() => setModal('add')} disabled={klienci.length === 0}>
          <span className="flex items-center gap-1.5"><IconPlus className="w-3.5 h-3.5" /> {t('spaces.add')}</span>
        </Btn>
      </div>

      {klienci.length === 0 && (
        <div className="rounded-xl p-4 mb-4" style={{ background: 'rgba(184,84,40,0.08)', border: '1px solid rgba(184,84,40,0.25)', fontSize: '11px', color: 'var(--acc2)' }}>
          {t('spaces.noClientWarning')}
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        {/* Search — w trybie compact ukrywany gdy ≤5 spacerów */}
        {(!compact || spacery.length > 5) && (
          <input
            className="ds-input flex-1 min-w-[200px]"
            placeholder={t('spaces.searchPlaceholder')}
            value={search} onChange={e => setSearch(e.target.value)}
          />
        )}
        <div className="flex gap-1 flex-wrap">
          {STATUSY_FILTER
            // W trybie compact ukryj statusy z count=0 (poza obecnie aktywnym + "Wszystkie")
            .filter(f => {
              if (!compact) return true;
              if (f.value === '' || filterStatus === f.value) return true;
              const n = f.value ? (counts[f.value] || 0) : (counts.all || 0);
              return n > 0;
            })
            .map(f => {
              const n = f.value ? (counts[f.value] || 0) : (counts.all || 0);
              const active = filterStatus === f.value;
              return (
                <button key={f.value} onClick={() => setFilterStatus(f.value)}
                  className="px-3 py-2 rounded-btn transition-colors flex items-center gap-1.5"
                  style={active
                    ? { background: 'var(--acc)', color: '#fff', border: '1px solid rgba(184,84,40,0.6)', fontSize: '9px', fontWeight: 700 }
                    : { background: 'var(--bg4)', color: 'var(--ink2)', border: '1px solid var(--border)', fontSize: '9px', fontWeight: 700 }}>
                  <span>{f.label}</span>
                  <span style={{
                    fontSize: '9px',
                    fontWeight: 700,
                    padding: '1px 6px',
                    borderRadius: '999px',
                    background: active ? 'rgba(255,255,255,0.22)' : 'var(--bg2)',
                    color: active ? '#fff' : 'var(--ink3)',
                    border: active ? '1px solid rgba(255,255,255,0.18)' : '1px solid var(--border)',
                    minWidth: '18px',
                    textAlign: 'center',
                    lineHeight: 1.4,
                  }}>{n}</span>
                </button>
              );
            })}
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} lines={2} />)}
        </div>
      ) : filtered.length === 0 ? (
        search ? (
          <EmptyState
            icon={<IconSearch className="w-12 h-12" />}
            title={t('spaces.noResults')}
            description={t('spaces.searchNoResultsHint', { query: search })}
            action={<Btn variant="ghost" onClick={() => setSearch('')}>{t('clients.clearSearch')}</Btn>}
          />
        ) : (
          <EmptyState
            icon={<IconBuilding className="w-12 h-12" />}
            title={t('spaces.noSpaces')}
            description={t('spaces.noSpacesHint')}
            action={
              <Btn onClick={() => setModal('add')}>
                <span className="flex items-center gap-1.5"><IconPlus className="w-3.5 h-3.5" /> {t('spaces.addFirst')}</span>
              </Btn>
            }
          />
        )
      ) : (
        <div className="flex flex-col gap-6">
          {/* Pakiety jako foldery */}
          {grouped.packages.map(pak => {
            const collapsed = !!collapsedPackages[pak.id];
            const used = pak.items.length;
            const cap  = pak.capacity || 0;
            const full = cap > 0 && used >= cap;
            return (
              <div key={pak.id} className="ds-card overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                <button
                  type="button"
                  onClick={() => setCollapsedPackages(c => ({ ...c, [pak.id]: !c[pak.id] }))}
                  className="w-full flex items-center justify-between px-4 py-3 text-left transition-colors hover:bg-navy-800"
                  style={{ background: 'rgba(184,84,40,0.08)', borderBottom: collapsed ? 'none' : '1px solid var(--border)' }}>
                  <div className="flex items-center gap-2 min-w-0">
                    <IconPackage className="w-4 h-4" style={{ color: 'var(--acc2)' }} />
                    <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--ink)' }} className="truncate">{pak.nazwa}</span>
                    {!defaultKlientId && pak.klient_nazwa && (
                      <span style={{ fontSize: '10px', color: 'var(--ink3)' }} className="truncate">· {pak.klient_nazwa}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="pill" style={full
                      ? { background: 'rgba(176,88,72,0.15)', color: 'var(--err2)', border: '1px solid rgba(176,88,72,0.3)' }
                      : { background: 'rgba(90,158,114,0.15)', color: 'var(--ok2)', border: '1px solid rgba(90,158,114,0.3)' }}>
                      {used}/{cap || '∞'} {t('spaces.spacery')}
                    </span>
                    <span style={{ color: 'var(--ink3)', fontSize: '12px', transform: collapsed ? 'rotate(0)' : 'rotate(90deg)', transition: 'transform 0.15s' }}>▶</span>
                  </div>
                </button>
                {!collapsed && (
                  <div className="flex flex-col gap-2 p-3" style={{ background: 'var(--bg2)' }}>
                    {pak.items.map(s => (
                      <SpacerCard key={s.id} spacer={s}
                        onEdit={() => setModal({ edit: s })}
                        onDelete={() => handleDelete(s)}
                        onNavigate={onNavigate}
                        defaultKlientId={defaultKlientId}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Spacery bez pakietu — flat */}
          {grouped.solo.length > 0 && (
            <div className="flex flex-col gap-4">
              {grouped.packages.length > 0 && (
                <h2 style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink3)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  {t('spaces.outsidePackage')}
                </h2>
              )}
              {grouped.solo.map(s => (
                <SpacerCard key={s.id} spacer={s}
                  onEdit={() => setModal({ edit: s })}
                  onDelete={() => handleDelete(s)}
                  onNavigate={onNavigate}
                  defaultKlientId={defaultKlientId}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {modal === 'add' && (
        <Modal title={t('spaces.add')} onClose={() => setModal(null)} wide>
          <SpacerForm klienci={klienci}
            initial={defaultKlientId ? { klient_id: defaultKlientId } : undefined}
            lockKlient={!!defaultKlientId}
            onSave={handleAdd} onClose={() => setModal(null)} />
        </Modal>
      )}
      {modal?.edit && (
        <Modal title={t('spaces.editSettings')} onClose={() => setModal(null)} wide>
          <SpacerForm klienci={klienci}
            initial={{ ...modal.edit, przelane_wykonanie: !!modal.edit.przelane_wykonanie }}
            onSave={handleEdit} onClose={() => setModal(null)} />
        </Modal>
      )}
    </div>
  );
}
